import { Box, Divider, H3, H4 } from '@deity/falcon-ui';
import { BaseHelmet, CreateEditStructureModal, EntityDetailsHeader } from 'components';
import { APOLLO_BASE_OPTIONS, CACHE_FIRST } from 'contracts/constants';
import type React from 'react';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useGetEntityUsageInfoQuery, useGetModelQuery } from 'services/graphql/generated/hooks';

type ModelOverViewProps = {
  params: { versionUuid: string; entityId: string };
  isEditable?: boolean;
};

export const StructureOverview: React.FC<ModelOverViewProps> = ({ params, isEditable }) => {
  const { pathname } = useLocation();
  const [modelModalOpen, setModelModalOpen] = useState<boolean>(false);
  const {
    loading: loadingModel,
    data: modelData,
    refetch
  } = useGetModelQuery({
    variables: {
      packageVersionUuid: params.versionUuid,
      uuid: params.entityId
    },
    ...CACHE_FIRST
  });
  const {
    loading: isLoading,
    data: infoData,
    refetch: refetchInfo
  } = useGetEntityUsageInfoQuery({
    variables: {
      versionUuid: params.versionUuid,
      entityType: 'schemas',
      entityKey: params.entityId
    },
    skip: !(params.versionUuid && params.entityId),
    ...APOLLO_BASE_OPTIONS
  });

  return (
    <>
      <BaseHelmet page={modelData?.getModel?.name ?? 'Structure'} />
      <EntityDetailsHeader
        isLoading={loadingModel}
        isEditable={isEditable}
        name={modelData?.getModel?.name}
        description={modelData?.getModel?.description}
        onModalOpen={setModelModalOpen}
        endEnhancer={
          <Box>
            <H3 color="secondary" fontWeight="bold" fontSize="xs" mt="md">
              Uuid:{modelData?.getModel?.uuid}
            </H3>
          </Box>
        }
      />
      <Divider />
      <Box p="xxl">
        {!isLoading && (
          <Box>
            <H4 fontSize="xl" fontWeight="bold" mr="xs">
              Used in {infoData?.getEntityUsageInfo.mappings.length} mapping
              {infoData?.getEntityUsageInfo.mappings.length === 1 ? '' : 's'}.
            </H4>
            <H3 color="secondary">This Structure is used in the following mappings.</H3>

            <Box borderRadius="small" p="xl" bg="lightGrey" mt="md">
              {infoData?.getEntityUsageInfo.mappings.map(item => (
                <H3 key={item.uuid} color="secondary" fontSize="lg">
                  Mapping : <Link to={`../mappings/${item.uuid}`}>{item.name}</Link>
                </H3>
              ))}
            </Box>
          </Box>
        )}
      </Box>
      <Divider />
      <Box p="xxl">
        {!isLoading && (
          <Box>
            <H4 fontSize="xl" fontWeight="bold" mr="xs">
              Used in {infoData?.getEntityUsageInfo.endpoints.length} source endpoint
              {infoData?.getEntityUsageInfo.endpoints.length === 1 ? '' : 's'}.
            </H4>
            <H3 color="secondary">This Structure is used in the following endpoints.</H3>

            <Box borderRadius="small" p="xl" bg="lightGrey" mt="md">
              {infoData?.getEntityUsageInfo.endpoints.map(item => (
                <H3 key={item.uuid} color="secondary" fontSize="lg">
                  Endpoint : <Link to={`../endpoints/${item.uuid}`}>{item.name}</Link>
                </H3>
              ))}
            </Box>
          </Box>
        )}
      </Box>
      <Divider />
      <Box p="xxl">
        {!isLoading && (
          <Box>
            <H4 fontSize="xl" fontWeight="bold" mr="xs">
              Used in {infoData?.getEntityUsageInfo.flows.length} flow
              {infoData?.getEntityUsageInfo.flows.length === 1 ? '' : 's'}.
            </H4>
            <H3 color="secondary">This Structure is used in the following flows.</H3>

            <Box borderRadius="small" p="xl" bg="lightGrey" mt="md">
              {infoData?.getEntityUsageInfo.flows.map(item => (
                <H3 key={item.uuid} color="secondary" fontSize="lg">
                  Endpoint : <Link to={`../flows/${item.uuid}`}>{item.name}</Link>
                </H3>
              ))}
            </Box>
          </Box>
        )}
      </Box>
      <CreateEditStructureModal
        versionUuid={params.versionUuid}
        isOpen={modelModalOpen}
        isEdit
        editModel={modelData?.getModel}
        setOpenState={state => setModelModalOpen(state)}
        onPackageCreated={() => {
          if (refetch) {
            refetch();
          }
        }}
      />
    </>
  );
};
